import { useGetOfferBatchLocationQuery, useGetSampleLocationQuery } from '@codezo/graphql';
import { LinkIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { EmptyState } from '../../components/ui/empty-state';
import { useSessionOrganizationId, useSwitchOrganization } from '../../hooks/useOrganizationSwitch';

/**
 * Resolver for `/n/:type/:id` — the one path the frontend has promised never to
 * move.
 *
 * Emails and notification-bell items never link to a real screen. They link
 * here with a `(type, id)` pair, and this decides where that pair currently
 * lives. An email's href is frozen the moment it lands in an inbox and may be
 * opened months later, so screens can move freely as long as this file keeps up.
 *
 * Nothing renders at `/n/…` in the normal case — it is a router, not a page.
 *
 * Adding a notification link type on the backend means adding a case here. See
 * `NotificationLinkType` in the backend's `notificationLink.ts` for the
 * authoritative list.
 *
 * Client emails add `?org=<organizationId>`. A user in several organizations
 * may be working in a different one than the link's, so the session is moved
 * there first and the link reloaded without the parameter — it then resolves
 * as usual, and can't trigger a second switch. Links without `?org=` (older
 * emails, the bell, which only ever shows the current organization) skip this.
 */

/** Where an unknown or unresolvable link lands, rather than erroring. */
const FALLBACK_PATH = '/dashboard';

export function NotificationLinkPage() {
	const { type, id } = useParams<{ type: string; id: string }>();
	const navigate = useNavigate();
	const sessionOrganizationId = useSessionOrganizationId();
	const [searchParams] = useSearchParams();
	const switchTo = useSwitchOrganization();

	const linkOrganizationId = searchParams.get('org');
	const needsSwitch = !!linkOrganizationId && linkOrganizationId !== sessionOrganizationId;

	// Keyed on the link rather than a plain flag, so a second `?org=` link
	// reaching this same mounted page still switches instead of hanging on
	// "Opening…" — and the ref still stops StrictMode's double effect from
	// switching twice.
	const switchKey = needsSwitch ? `${linkOrganizationId}|${type}|${id}` : null;
	const startedFor = useRef<string | null>(null);
	const [refusedFor, setRefusedFor] = useState<string | null>(null);

	useEffect(() => {
		if (!switchKey || !linkOrganizationId || startedFor.current === switchKey) return;
		startedFor.current = switchKey;
		const rest = new URLSearchParams(searchParams);
		rest.delete('org');
		const query = rest.toString();
		const destination = `/n/${type}/${id}${query ? `?${query}` : ''}`;
		// Refused means no longer a member — that's the unavailable screen, not
		// an error toast.
		switchTo(linkOrganizationId, destination).catch(() => setRefusedFor(switchKey));
	}, [switchKey, linkOrganizationId, searchParams, switchTo, type, id]);

	// Offers are reviewed on the quote screen, so a batch id has to be traded for
	// its quote id first. Only the matching type's query runs.
	const batch = useGetOfferBatchLocationQuery({
		variables: { id: id ?? '' },
		skip: type !== 'offer-batch' || !id || needsSwitch,
		fetchPolicy: 'no-cache',
	});

	// Samples live on an order, and nothing else in the API gets from a sample
	// id to one.
	const sample = useGetSampleLocationQuery({
		variables: { id: id ?? '' },
		skip: type !== 'sample' || !id || needsSwitch,
		fetchPolicy: 'no-cache',
	});

	const unavailable = (
		<div className="mx-auto max-w-md px-4 py-24">
			<EmptyState
				icon={LinkIcon}
				title="This is no longer available"
				description="The link may have expired, or it belongs to an account you're not signed in to."
			>
				<Button className="mt-2" onClick={() => navigate(FALLBACK_PATH, { replace: true })}>
					Go to dashboard
				</Button>
			</EmptyState>
		</div>
	);

	if (!id) return <Navigate to={FALLBACK_PATH} replace />;
	if (needsSwitch) return refusedFor === switchKey ? unavailable : <ResolvingScreen />;

	switch (type) {
		case 'order':
			// A permalink to the order, whatever state it has reached. The order
			// page owns "no such order" — no point paying for a lookup here.
			return <Navigate to={`/orders/${id}`} replace />;

		case 'quote':
			// Carries OFFER_LATE_ACCEPTANCE, which is internal — a client is
			// unlikely to arrive here. Handled anyway: the type exists in the
			// contract, and a link that resolves nowhere is worse than one nobody
			// follows.
			return <Navigate to={`/quotes/${id}`} replace />;

		case 'sample': {
			if (sample.loading) return <ResolvingScreen />;
			// One indistinguishable null covers gone, not-yours and never-existed,
			// so that a client can't enumerate other organisations' samples by id.
			// Nothing here should try to tell those apart.
			const orderId = sample.data?.getSampleLocation?.orderId;
			if (sample.error || !orderId) return unavailable;
			// The order page is where a sample is approved or rejected. It has no
			// anchor for an individual sample yet — `orderItemId` is what that
			// would need, and the query already returns it.
			return <Navigate to={`/orders/${orderId}`} replace />;
		}

		case 'offer-batch': {
			if (batch.loading) return <ResolvingScreen />;
			// Withdrawn offers and people who changed organisation both land here.
			const quoteId = batch.data?.getOfferBatchLocation?.orderQuoteId;
			if (batch.error || !quoteId) return unavailable;
			// Resolves even once accepted — a permalink to the thing, not the action.
			return <Navigate to={`/quotes/${quoteId}`} replace />;
		}

		default:
			// Backends deploy ahead of frontends, so this build may not know the
			// type yet. Somewhere useful beats an error page.
			return <Navigate to={FALLBACK_PATH} replace />;
	}
}

function ResolvingScreen() {
	return (
		<div className="flex min-h-screen items-center justify-center">
			<p className="text-sm text-text-3">Opening…</p>
		</div>
	);
}
